/**
 * Pattern Data Sync Service
 * Addresses localStorage persistence limitations by syncing pattern data to Supabase
 * Enables cross-device, cross-browser pattern continuity while maintaining privacy
 */

import { supabase } from './supabase.js';

class PatternSyncService {
  constructor() {
    this.syncInterval = null;
    this.lastSyncTime = null;
    this.syncInProgress = false;
    this.maxRetries = 3;
    this.syncIntervalMs = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Initialize pattern sync for a user
   */
  async initializeSync(userId) {
    try {
      // Load existing patterns from server on app start
      await this.loadPatternsFromServer(userId);
      
      // Start periodic sync
      this.startPeriodicSync(userId);
      
      // Set up beforeunload sync
      this.setupBeforeUnloadSync(userId);
      
      console.log('Pattern sync initialized for user:', userId);
      return true;
    } catch (error) {
      console.warn('Failed to initialize pattern sync:', error);
      return false;
    }
  }

  /**
   * Load patterns from server and merge with local data
   */
  async loadPatternsFromServer(userId) {
    try {
      const { data: serverPatterns, error } = await supabase
        .from('user_patterns')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (serverPatterns && serverPatterns.length > 0) {
        // Get local patterns
        const localPatterns = this.getLocalPatterns(userId);
        
        // Merge server and local patterns, removing duplicates
        const mergedPatterns = this.mergePatterns(serverPatterns, localPatterns);
        
        // Update local storage with merged data
        this.saveLocalPatterns(userId, mergedPatterns);
        
        console.log(`Loaded ${serverPatterns.length} patterns from server, merged with ${localPatterns.length} local patterns`);
      }
    } catch (error) {
      console.warn('Failed to load patterns from server:', error);
      // Continue with local patterns only
    }
  }

  /**
   * Sync local patterns to server
   */
  async syncPatternsToServer(userId) {
    if (this.syncInProgress) return false;
    
    try {
      this.syncInProgress = true;
      const localPatterns = this.getLocalPatterns(userId);
      
      if (localPatterns.length === 0) {
        this.syncInProgress = false;
        return true;
      }

      // Get last sync time to only sync new patterns
      const lastSyncTime = localStorage.getItem(`lastPatternSync_${userId}`);
      const patternsToSync = lastSyncTime 
        ? localPatterns.filter(p => new Date(p.timestamp) > new Date(lastSyncTime))
        : localPatterns.slice(-50); // Only sync last 50 if no previous sync

      if (patternsToSync.length === 0) {
        this.syncInProgress = false;
        return true;
      }

      // Prepare patterns for database
      const dbPatterns = patternsToSync.map(pattern => ({
        user_id: userId,
        action: pattern.action,
        context_data: pattern.context || {},
        metadata: pattern.metadata || {},
        timestamp: pattern.timestamp,
        created_at: pattern.timestamp,
        pattern_hash: this.generatePatternHash(pattern) // Prevent duplicates
      }));

      // Upsert patterns (insert new, ignore duplicates)
      const { error } = await supabase
        .from('user_patterns')
        .upsert(dbPatterns, { 
          onConflict: 'user_id,pattern_hash',
          ignoreDuplicates: true 
        });

      if (error) throw error;

      // Update last sync time
      localStorage.setItem(`lastPatternSync_${userId}`, new Date().toISOString());
      this.lastSyncTime = new Date();
      
      console.log(`Synced ${patternsToSync.length} patterns to server`);
      this.syncInProgress = false;
      return true;
    } catch (error) {
      console.warn('Failed to sync patterns to server:', error);
      this.syncInProgress = false;
      return false;
    }
  }

  /**
   * Merge server and local patterns, handling duplicates intelligently
   */
  mergePatterns(serverPatterns, localPatterns) {
    // Convert server patterns to local format
    const serverPatternsLocal = serverPatterns.map(sp => ({
      action: sp.action,
      context: sp.context_data || {},
      metadata: sp.metadata || {},
      timestamp: sp.timestamp,
      hash: sp.pattern_hash
    }));

    // Create a map of existing patterns by hash
    const patternMap = new Map();
    
    // Add server patterns first (they're authoritative)
    serverPatternsLocal.forEach(pattern => {
      if (pattern.hash) {
        patternMap.set(pattern.hash, pattern);
      }
    });

    // Add local patterns that don't exist on server
    localPatterns.forEach(pattern => {
      const hash = this.generatePatternHash(pattern);
      if (!patternMap.has(hash)) {
        patternMap.set(hash, { ...pattern, hash });
      }
    });

    // Convert back to array and sort by timestamp
    return Array.from(patternMap.values())
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }

  /**
   * Generate a hash for pattern deduplication
   */
  generatePatternHash(pattern) {
    const hashData = {
      action: pattern.action,
      timestamp: pattern.timestamp,
      userId: pattern.userId || pattern.user_id
    };
    
    // Simple hash function for deduplication
    return btoa(JSON.stringify(hashData)).slice(0, 16);
  }

  /**
   * Get local patterns from localStorage
   */
  getLocalPatterns(userId) {
    try {
      const patterns = localStorage.getItem(`userPatterns_${userId}`);
      return patterns ? JSON.parse(patterns) : [];
    } catch (error) {
      console.warn('Failed to get local patterns:', error);
      return [];
    }
  }

  /**
   * Save patterns to localStorage
   */
  saveLocalPatterns(userId, patterns) {
    try {
      // Keep only last 500 patterns to prevent localStorage bloat
      const trimmedPatterns = patterns
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
        .slice(0, 500);
        
      localStorage.setItem(`userPatterns_${userId}`, JSON.stringify(trimmedPatterns));
      return true;
    } catch (error) {
      console.warn('Failed to save local patterns:', error);
      return false;
    }
  }

  /**
   * Start periodic background sync
   */
  startPeriodicSync(userId) {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    this.syncInterval = setInterval(async () => {
      await this.syncPatternsToServer(userId);
    }, this.syncIntervalMs);
  }

  /**
   * Set up sync before page unload
   */
  setupBeforeUnloadSync(userId) {
    const syncOnUnload = async () => {
      // Use sendBeacon for reliable sync on page unload
      try {
        await this.syncPatternsToServer(userId);
      } catch (error) {
        console.warn('Failed to sync on unload:', error);
      }
    };

    window.addEventListener('beforeunload', syncOnUnload);
    window.addEventListener('pagehide', syncOnUnload);
    
    // Also sync on visibility change (when user switches tabs)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        syncOnUnload();
      }
    });
  }

  /**
   * Manual sync trigger
   */
  async forceSyncNow(userId) {
    console.log('Force syncing patterns...');
    const success = await this.syncPatternsToServer(userId);
    if (success) {
      console.log('Force sync completed successfully');
    } else {
      console.warn('Force sync failed');
    }
    return success;
  }

  /**
   * Clean up sync resources
   */
  stopSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Get sync status information
   */
  getSyncStatus(userId) {
    const lastSyncTime = localStorage.getItem(`lastPatternSync_${userId}`);
    const localPatternCount = this.getLocalPatterns(userId).length;
    
    return {
      lastSyncTime: lastSyncTime ? new Date(lastSyncTime) : null,
      localPatternCount,
      syncInProgress: this.syncInProgress,
      isInitialized: this.syncInterval !== null
    };
  }

  /**
   * Export patterns for backup/migration
   */
  async exportPatterns(userId) {
    try {
      // Get both local and server patterns
      const localPatterns = this.getLocalPatterns(userId);
      
      const { data: serverPatterns } = await supabase
        .from('user_patterns')
        .select('*')
        .eq('user_id', userId);

      const exportData = {
        exportDate: new Date().toISOString(),
        userId,
        localPatterns,
        serverPatterns: serverPatterns || [],
        totalPatterns: (localPatterns.length + (serverPatterns?.length || 0))
      };

      return exportData;
    } catch (error) {
      console.error('Failed to export patterns:', error);
      throw error;
    }
  }

  /**
   * Import patterns from backup
   */
  async importPatterns(userId, exportData) {
    try {
      if (exportData.localPatterns) {
        // Merge with existing local patterns
        const existingPatterns = this.getLocalPatterns(userId);
        const mergedPatterns = this.mergePatterns(exportData.localPatterns, existingPatterns);
        this.saveLocalPatterns(userId, mergedPatterns);
      }

      // Force sync to server
      await this.syncPatternsToServer(userId);
      
      console.log('Patterns imported successfully');
      return true;
    } catch (error) {
      console.error('Failed to import patterns:', error);
      return false;
    }
  }
}

export const patternSyncService = new PatternSyncService();